import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/dexie';
import { untilQuiet } from '../sync/activity';
import { describeError, phaseFor, runSync, type SyncPhase, type SyncResult } from '../sync/engine';
import { isConfigured } from '../sync/supabase';

/** After a local write, wait this long for the typing to stop before pushing. */
const DEBOUNCE_MS = 2000;
/** Backstop for changes made on the other device while this one sits open. */
const POLL_MS = 60_000;

export interface SyncStatus {
  phase: SyncPhase;
  lastSyncedAt: string | null;
  pending: number;
  /** Why the last cycle failed, in words. Null when the last cycle was fine. */
  error: string | null;
  syncNow: () => void;
}

export function useSync(signedIn: boolean): SyncStatus {
  const [phase, setPhase] = useState<SyncPhase>(isConfigured ? 'idle' : 'disabled');
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);
  const queued = useRef(false);
  /** A cycle put off because a note was being written. */
  const waiting = useRef<number | null>(null);

  // Rows waiting to be pushed. Drives the "syncing / synced" wording, and is
  // the honest answer to "is my work safe yet".
  const pending = useLiveQuery(
    async () => {
      const counts = await Promise.all([
        db.items.where('dirty').equals(1).count(),
        db.lists.where('dirty').equals(1).count(),
        db.listTags.where('dirty').equals(1).count(),
        db.globalTags.where('dirty').equals(1).count(),
        db.attachments.where('dirty').equals(1).count(),
      ]);
      return counts.reduce((a: number, b: number) => a + b, 0);
    },
    [],
    0,
  );

  const syncNow = useCallback(() => {
    if (!isConfigured || !signedIn) return;
    if (running.current) {
      // Never run two cycles at once; remember that another was asked for.
      queued.current = true;
      return;
    }

    // Never sync while a note is being written. A pull that lands mid-sentence
    // is the one thing that can put text into the editor the user did not type,
    // and the copy it carries is necessarily older than what is on screen. The
    // work is not skipped, only held until the typing stops — including the
    // timer poll, which would otherwise fire straight through a long note.
    const quiet = untilQuiet();
    if (quiet > 0) {
      if (waiting.current === null) {
        waiting.current = window.setTimeout(() => {
          waiting.current = null;
          syncNow();
        }, quiet);
      }
      return;
    }

    running.current = true;
    setPhase('syncing');

    void (async () => {
      try {
        const result: SyncResult = await runSync();
        setLastSyncedAt(new Date().toISOString());
        setError(null);
        setPhase('idle');
        if (result.conflicts > 0) {
          console.info(`[sync] ${result.conflicts} conflict(s) recorded — see the conflict log`);
        }
      } catch (cause) {
        const message = describeError(cause);
        setError(message);
        setPhase(phaseFor(cause));
        console.error(`[sync] ${message}`, cause);
      } finally {
        running.current = false;
        if (queued.current) {
          queued.current = false;
          syncNow();
        }
      }
    })();
  }, [signedIn]);

  // On start, on foreground, on reconnect.
  useEffect(() => {
    if (!signedIn) {
      setPhase(isConfigured ? 'signed-out' : 'disabled');
      return;
    }
    syncNow();

    const onVisible = () => document.visibilityState === 'visible' && syncNow();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', syncNow);
    const timer = window.setInterval(syncNow, POLL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', syncNow);
      window.clearInterval(timer);
      if (waiting.current !== null) {
        window.clearTimeout(waiting.current);
        waiting.current = null;
      }
    };
  }, [signedIn, syncNow]);

  // After any local write, debounced.
  useEffect(() => {
    if (!signedIn || pending === 0) return;
    const timer = window.setTimeout(syncNow, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [pending, signedIn, syncNow]);

  return { phase, lastSyncedAt, pending, error, syncNow };
}

/** The one word the sidebar shows. Quiet by design — status is not content. */
export function syncLabel({ phase, pending }: Pick<SyncStatus, 'phase' | 'pending'>): string {
  switch (phase) {
    case 'syncing':
      return 'syncing';
    case 'offline':
      return pending > 0 ? `offline · ${pending}` : 'offline';
    case 'error':
      return 'sync failed';
    case 'signed-out':
      return 'signed out';
    case 'disabled':
      return 'local only';
    default:
      return pending > 0 ? `pending ${pending}` : 'synced';
  }
}
